import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService } from '@services/loadInitialData';
import { collectionRef, Expression } from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';

type ExpressionResourceType = 'form' | 'form-group';

type LegacyExpressionFields = {
  source_id?: string;
  source_type?: ExpressionResourceType;
};

const expressionWithLegacyFields = (exp: Expression) => exp as Expression & LegacyExpressionFields;

/** Prefers `resource.id`, falls back to legacy `source_id`. */
export const getExpressionResourceId = (exp: Expression): string | undefined =>
  expressionWithLegacyFields(exp).resource?.id ?? expressionWithLegacyFields(exp).source_id;

export const expressionBelongsTo = (exp: Expression, resourceId?: string | null) =>
  !!resourceId && getExpressionResourceId(exp) === resourceId;

function expressionsFactory() {
  const loadExpressionsStore = async (onProgress: (expressions: Expression[]) => void) => {
    let inFlightStore: Expression[] = [];

    await InitialDataService.load<Expression>(collectionRef.expressions, [], {}, (expressions) => {
      inFlightStore = expressions;
      onProgress(inFlightStore);
    });

    return inFlightStore;
  };

  const getExpressionsByScopeAndSource = async (scope: Expression['scope'], sourceId: string) => {
    return collectionsService.expressions.getDocs([
      where('scope', '==', scope),
      where('resource.id', '==', sourceId)
    ]);
  };

  const getRuntimeExpressionsBySourceIds = async (sourceIds: string[]) => {
    return collectionsService.expressions.getDocs([
      where('scope', '==', 'runtime'),
      where('resource.id', 'in', sourceIds)
    ]);
  };

  const getExpressionsByEmailTrigger = async (emailTrigger: string) => {
    return collectionsService.expressions.getDocs([where('email_trigger', '==', emailTrigger)]);
  };

  return {
    getExpressionsByScopeAndSource,
    loadExpressionsStore,
    getExpressionsByEmailTrigger,
    getRuntimeExpressionsBySourceIds
  };
}

export const expressionsService = expressionsFactory();
